import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Share2, X as Twitter, Facebook, Linkedin, Copy, Check, Mail, MessageCircle, Send } from 'lucide-react';

// Reddit icon component (not in lucide-react)
const RedditIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

// HackerNews icon
const HackerNewsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M0 24V0h24v24H0zM6.951 5.896l4.112 7.708v5.064h1.583v-4.972l4.148-7.799h-1.749l-2.457 4.875c-.372.745-.688 1.434-.688 1.434s-.297-.708-.651-1.434L8.831 5.896h-1.88z"/>
  </svg>
);

export default function ShareButton({ title, description, url, itemId, itemType, className = "" }) {
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Generate specific item URL if itemId and itemType are provided
  const baseUrl = window.location.origin;
  const shareUrl = itemId && itemType
    ? `${baseUrl}?item=${itemId}&type=${itemType}`
    : (url || window.location.href);

  // Use different prefix based on content type
  const prefix = itemType === 'story' ? 'Bar tale' : 'Drnk startup idea';
  const shareTitle = `${prefix}: ${title}`;
  const shareText = `${description}\n\n${shareUrl}`;
  const fullText = `${prefix}: ${title}\n${description}\n${shareUrl}`;

  // Calculate position when menu opens
  useEffect(() => {
    if (showMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 200;
      const menuHeight = 400;

      // Position below the button by default
      let top = rect.bottom + 8;
      let left = rect.left;

      // If menu would go off bottom of screen, position above
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 8;
      }

      // If menu would go off right of screen, align to right edge
      if (left + menuWidth > window.innerWidth) {
        left = window.innerWidth - menuWidth - 8;
      }

      // If menu would go off left of screen
      if (left < 8) {
        left = 8;
      }

      setMenuPosition({ top, left });
    }
  }, [showMenu]);

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);

    // For X/Twitter: Include title, description, and link in the text
    const tweetText = `${prefix}: ${title}\n\n${description}\n\n${shareUrl}`;

    const urls = {
      twitter: `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(tweetText)}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(`${prefix}: ${title}\n\n${description}`)}`,
      hackernews: `https://news.ycombinator.com/submitlink?u=${encodedUrl}&t=${encodedTitle}`,
      email: `mailto:?subject=${encodedTitle}&body=${encodeURIComponent(`${description}\n\n${shareUrl}`)}`
    };

    // Platform-specific window sizes for optimal viewing
    const windowSizes = {
      twitter: 'width=550,height=600',
      facebook: 'width=600,height=700',
      linkedin: 'width=600,height=700',
      reddit: 'width=800,height=700',
      whatsapp: 'width=600,height=600',
      telegram: 'width=600,height=600',
      hackernews: 'width=700,height=600',
      email: '' // Email opens in default mail client
    };

    if (urls[platform]) {
      const windowFeatures = windowSizes[platform] || 'width=600,height=600';
      if (platform === 'email') {
        window.location.href = urls[platform];
      } else {
        window.open(urls[platform], '_blank', windowFeatures);
      }
      setShowMenu(false);
    }
  };

  const handleCopy = async () => {
    try {
      const textToCopy = `${prefix}: ${title}\n${description}\n${shareUrl}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        setShowMenu(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const menuContent = showMenu && (
    <>
      <div
        className="fixed inset-0 z-[9998]"
        onClick={() => setShowMenu(false)}
      />
      <div
        className="fixed z-[9999] bg-slate-800 border border-slate-700 rounded-lg shadow-2xl p-2 min-w-[200px]"
        style={{
          top: `${menuPosition.top}px`,
          left: `${menuPosition.left}px`
        }}
      >
            <button
              onClick={() => handleShare('twitter')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-slate-700 rounded transition"
            >
              <Twitter className="w-4 h-4" />
              X
            </button>

            <button
              onClick={() => handleShare('linkedin')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-slate-700 rounded transition"
            >
              <Linkedin className="w-4 h-4" />
              LinkedIn
            </button>

            <button
              onClick={() => handleShare('facebook')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-slate-700 rounded transition"
            >
              <Facebook className="w-4 h-4" />
              Facebook
            </button>

            <button
              onClick={() => handleShare('reddit')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-slate-700 rounded transition"
            >
              <RedditIcon className="w-4 h-4" />
              Reddit
            </button>

            <button
              onClick={() => handleShare('hackernews')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-slate-700 rounded transition"
            >
              <HackerNewsIcon className="w-4 h-4" />
              HackerNews
            </button>

            <div className="border-t border-slate-700 my-1" />

            <button
              onClick={() => handleShare('whatsapp')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-slate-700 rounded transition"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </button>

            <button
              onClick={() => handleShare('telegram')}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-slate-700 rounded transition"
            >
              <Send className="w-4 h-4" />
              Telegram
            </button>

            <div className="border-t border-slate-700 my-1" />

            <button
              onClick={handleCopy}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-white hover:bg-slate-700 rounded transition"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-green-400">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Link
                </>
              )}
            </button>
          </div>
        </>
      );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setShowMenu(!showMenu)}
        className={`flex items-center gap-1 text-slate-400 hover:text-white transition ${className}`}
      >
        <Share2 className="w-3 h-3" />
        <span>Share</span>
      </button>
      {menuContent && createPortal(menuContent, document.body)}
    </>
  );
}
